import { useState, useCallback, useRef, useEffect } from "react"
import type { TamCalculatorFilters, TamCalculatorResults } from "~/react/types/tam_calculator"
import { tam_calculator_path } from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import { buildTamFilterParams, hasActiveTamFilters } from "~/react/utils/tamCalculatorFilters"

interface UseTamCalculatorResultsProps {
  initialResults: TamCalculatorResults | null
  initialHasFilters: boolean
  initialAcv?: number | null
  initialTam?: number | null
}

export function useTamCalculatorResults({
  initialResults,
  initialHasFilters,
  initialAcv = null,
  initialTam = null,
}: UseTamCalculatorResultsProps) {
  const [results, setResults] = useState<TamCalculatorResults | null>(initialResults)
  const [isLoading, setIsLoading] = useState(false)
  const [hasFilters, setHasFilters] = useState(initialHasFilters)
  const [acv, setAcv] = useState<number | null>(initialAcv)
  const [tam, setTam] = useState<number | null>(initialTam)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Cleanup on unmount - abort any in-flight requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchResults = useCallback(async (filters: TamCalculatorFilters) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    // Build query string from filters
    const params = buildTamFilterParams(filters)

    const newHasFilters = hasActiveTamFilters(filters)

    // Update URL without reload
    const queryString = params.toString()
    const url = queryString ? `${tam_calculator_path()}?${queryString}` : tam_calculator_path()
    window.history.replaceState(null, "", url)

    // Extract ACV for state management
    const acvValue = filters.acv || null
    setAcv(acvValue)

    // If no filters, clear results
    if (!newHasFilters) {
      setResults(null)
      setHasFilters(false)
      setTam(null)
      return
    }

    setIsLoading(true)
    setHasFilters(true)

    try {
      // Add format=json to get JSON response
      params.append("format", "json")

      const response = await fetch(`${tam_calculator_path()}?${params.toString()}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        signal: abortControllerRef.current.signal,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      const resultsData: TamCalculatorResults = {
        domains: data.results?.domains || [],
        numberOfCompanies: data.numberOfCompanies || 0,
        tam: data.tam,
      }

      setResults(resultsData)
      setTam(data.tam)
    } catch (error) {
      // Ignore abort errors - they're expected when a new request cancels the previous one
      if (error instanceof Error && error.name === "AbortError") {
        return
      }
      console.error("Failed to fetch TAM calculator results:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clearResults = useCallback(() => {
    setResults(null)
    setHasFilters(false)
    setAcv(null)
    setTam(null)
    window.history.replaceState(null, "", tam_calculator_path())
  }, [])

  return {
    results,
    isLoading,
    hasFilters,
    acv,
    tam,
    fetchResults,
    clearResults,
  }
}
